import { Component, Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Outlet, useRoutes, useParams, useLocation } from 'react-router-dom';
import { auth } from '@/features/auth/firebase';
import { AuthState } from 'src/constants/auth';
import {
  isReservedAppPrefix,
  loginRedirectTo,
  normalizeTenantRedirectPath,
  resolveTenantCode,
  toTenantPath,
  withLocationParams
} from 'src/lib/tenantRouting';

// layouts
// import MainLayout from '../layouts/main';
import {
  GuestGuard,
  AuthenticatedUserGuard,
  PlatformAdminGuard,
  TenantRoleGuard,
  PremiumTenantGuard,
  OrgDashboardGuard,
  TenantFeatureGuard
} from 'src/app/routes/guards';
import FeatureFlagGuard from 'src/app/routes/FeatureFlagGuard';
import AppLayout from '../components/layouts/AppLayout';
import Loading from '../components/ui/Loading';
import HomeLayout from '../features/home';
import EmailVerification from 'src/features/auth/EmailVerification';
import MfaVerification from 'src/features/auth/MfaVerification';
// hooks
import useAuth from '../hooks/useAuth';
import TenantPickerPage from '../features/auth/TenantPickerPage';
import {
  ADMIN_TENANT_MEMBERSHIP_ROLES,
  ALL_TENANT_MEMBERSHIP_ROLES,
  CHAMPION_TENANT_MEMBERSHIP_ROLES,
  NON_GUEST_TENANT_MEMBERSHIP_ROLES
} from 'src/types/auth';
// ----------------------------------------------------------------------

const preloadReloadKey = 'vite-preload-error-reload';

const isLazyChunkError = (error) =>
  error instanceof Error &&
  ['Failed to fetch dynamically imported module', 'Importing a module script failed'].some((message) =>
    error.message.includes(message)
  );

class LazyChunkErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isLazyChunkError(error) || sessionStorage.getItem(preloadReloadKey)) return;
    sessionStorage.setItem(preloadReloadKey, 'true');
    window.location.reload();
  }

  render() {
    const { error } = this.state;
    if (error) {
      // Only swallow recoverable deploy/chunk mismatches; let real bugs bubble.
      if (!isLazyChunkError(error)) {
        throw error;
      }
      return <Loading fullscreen />;
    }

    return this.props.children;
  }
}

function DelayedLoading() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;
  return <Loading fullscreen />;
}

const Loadable = (LoadedComponent) => (props) => {
  return (
    <LazyChunkErrorBoundary>
      <Suspense fallback={<DelayedLoading />}>
        <LoadedComponent {...props} />
      </Suspense>
    </LazyChunkErrorBoundary>
  );
};

// Component to handle root redirect based on authentication status
const RootRedirect = () => {
  const { authState, isAuthenticated, isInitialized, completeMfa, user } = useAuth();
  const location = useLocation();
  if (!isInitialized) {
    return null;
  }

  // Handle email verification state
  if (authState === AuthState.EMAIL_UNVERIFIED) {
    return <EmailVerification />;
  }

  // Handle MFA required state for admin users
  if (authState === AuthState.MFA_REQUIRED) {
    return <MfaVerification onVerified={completeMfa} />;
  }

  if (isAuthenticated && user?.tenants?.length === 0) {
    return <Navigate to="/no-tenant" replace />;
  }

  const tenantCode = resolveTenantCode(location.pathname, user?.tenants) || null;

  if (isAuthenticated) {
    // Check if this is a legacy URL — the first path segment is a known app prefix
    // (e.g. /chat/01abc..., /review/...) but has no tenant prefix. This happens when
    // React Router can't match /:tenantCode because no child handles the remaining
    // segment, so the * catch-all fires here instead.
    const firstSegment = location.pathname.split('/').filter(Boolean)[0] ?? '';
    const isLegacyAppPath = isReservedAppPrefix(firstSegment);

    if (isLegacyAppPath) {
      const tenants = user?.tenants ?? [];
      const returnPath = withLocationParams(location.pathname, location);
      if (tenants.length > 1) {
        // Always show the picker for multi-tenant users — the stored active tenant
        // is just the last-used one and shouldn' t silently decide for them.
        return <TenantPickerPage tenants={tenants} originalPath={returnPath} />;
      }
      if (tenants.length === 1) {
        return <Navigate to={toTenantPath(returnPath, tenants[0].code)} replace />;
      }
      // 0 tenants — fall through to /no-tenant redirect below
    }

    return <Navigate to={normalizeTenantRedirectPath('/home', tenantCode)} replace />;
  }

  if (auth.currentUser) return null;

  return <Navigate to="/auth/login" replace />;
};

const TenantHomeRedirect = () => {
  const location = useLocation();
  return <Navigate to={toTenantPath('/home', resolveTenantCode(location.pathname))} replace />;
};

export default function Router() {
  return useRoutes([
    {
      // Public freemium self-service signup - no authentication required
      path: '/auth/signup',
      element: <FreemiumSignup />
    },
    {
      path: '/auth/register-forum',
      element: <RegisterForum />
    },
    {
      // Main registration route with quiz flow
      path: '/auth/register',
      element: <Register />
    },
    {
      path: '/microsoft-auth-callback',
      element: <MicrosoftAuthCallback />
    },
    {
      // Public unsubscribe page for group reminders - no authentication required
      path: '/unsubscribe/group-reminders',
      element: <UnsubscribeRemindersPage />
    },
    {
      // Email change verification - accessible without auth since user needs to verify before login
      path: '/account/verify-email-change',
      element: <VerifyEmailChange />
    },
    {
      // Freemium signup email verification - accessible without auth
      path: '/auth/verify-email',
      element: <VerifyFreemiumEmail />
    },
    { path: 'auth/forgot-password', element: <ForgotPassword /> },
    { path: 'auth/action-handler', element: <FirebaseActionHandler /> },
    {
      path: '/auth',
      element: <GuestGuard />,
      children: [
        { path: 'login', element: <Login /> },
        { path: 'login-sso', element: <LoginSSO /> }
      ]
    },
    {
      element: <AuthenticatedUserGuard />,
      children: [
        {
          path: '/no-tenant',
          element: <NoTenantPage />
        },
        {
          path: '/profile/*',
          element: <ProfileToSettingsRedirect />
        },
        {
          path: '/tutorials',
          element: <AppLayout />,
          children: [
            {
              element: <HelpLayout />,
              children: [
                { path: '', element: <HelpHome /> },
                { path: ':slug', element: <TutorialDetail /> }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/admin',
      element: <PlatformAdminGuard />,
      children: [
        {
          element: <AppLayout />,
          children: [{ path: '', element: <AdminHome /> }]
        }
      ]
    },
    { path: '/data-library', element: <Navigate to="/admin?tab=data-library" replace /> },
    {
      path: '/:tenantCode',
      element: <TenantPrefixGuard />,
      children: [
        { index: true, element: <Navigate to="home" replace /> },
        // Free routes — accessible to all subscription tiers
        {
          element: <TenantRoleGuard accessibleRoles={NON_GUEST_TENANT_MEMBERSHIP_ROLES} />,
          children: [{ path: 'landing', element: <ChatLandingPage /> }]
        },
        {
          element: <AppLayout />,
          children: [
            {
              element: <TenantRoleGuard accessibleRoles={ALL_TENANT_MEMBERSHIP_ROLES} />,
              children: [
                { path: 'home', element: <HomeLayout /> },
                { path: 'chat/:sessionId?', element: <ChatLayout /> },
                {
                  path: 'settings',
                  element: <UserProfile />,
                  children: [
                    { path: '', element: <EditProfile /> },
                    { path: 'appointments', element: <ProfileAppointments /> },
                    { path: 'appointments-certificates', element: <Navigate to="../appointments" replace /> },
                    { path: 'update-password', element: <Navigate to=".." replace /> },
                    { path: 'notifications', element: <NotificationsPage /> },
                    {
                      element: <FeatureFlagGuard flagKey="activity_feed" redirectTo=".." />,
                      children: [{ path: 'activity', element: <ActivityFeed /> }]
                    },
                    { path: 'delete-account', element: <Navigate to=".." replace /> },
                    { path: 'help-support', element: <HelpSupport /> },
                    { path: 'organisation', element: <OrganisationSettingsPage section="general" /> },
                    { path: 'members', element: <OrganisationSettingsPage section="members" /> },
                    { path: 'guests', element: <OrganisationSettingsPage section="guests" /> },
                    { path: 'sites', element: <OrganisationSettingsPage section="sites" /> },
                    {
                      element: <FeatureFlagGuard flagKey="domain-toggle" redirectTo=".." />,
                      children: [{ path: 'domains', element: <OrganisationSettingsPage section="domains" /> }]
                    },
                    { path: 'ai-controls', element: <OrganisationSettingsPage section="ai-controls" /> }
                  ]
                }
              ]
            },
            // Premium routes — freemium users are redirected to home
            {
              element: <PremiumTenantGuard />,
              children: [
                {
                  element: <TenantRoleGuard accessibleRoles={NON_GUEST_TENANT_MEMBERSHIP_ROLES} />,
                  children: [
                    { path: 'wiki', element: <WikiLayout /> },
                    { path: 'write', element: <WritePage /> },
                    { path: 'review', element: <ReviewHome /> },
                    { path: 'reporting', element: <ReportingHome /> },
                    {
                      element: <PlatformAdminGuard />,
                      children: [{ path: 'data-library', element: <Navigate to="/admin?tab=data-library" replace /> }]
                    },
                    { path: 'documents/*', element: <DocumentHubPage /> },
                    { path: 'documents/alerts', element: <DocumentHubAlertsPage /> },
                    { path: 'modules', element: <TenantHomeRedirect /> },
                    {
                      path: 'modules/pfi',
                      element: <TenantFeatureGuard featureKey="pfi_module" redirectTo="/home" />,
                      children: [
                        { path: '', element: <PFIModulePage /> },
                        { path: 'asset-review', element: <AssetReviewListPage /> },
                        { path: 'asset-review/:id', element: <AssetReviewDetailPage /> },
                        {
                          element: <TenantRoleGuard accessibleRoles={ADMIN_TENANT_MEMBERSHIP_ROLES} />,
                          children: [{ path: 'obligations', element: <ObligationRegisterPage /> }]
                        }
                      ]
                    },
                    {
                      path: 'modules/training-hub',
                      element: <TenantRoleGuard accessibleRoles={ADMIN_TENANT_MEMBERSHIP_ROLES} />,
                      children: [
                        {
                          path: '',
                          element: <TenantFeatureGuard featureKey="training_hub" redirectTo="/home" />,
                          children: [
                            { path: '', element: <TrainingHubPage /> },
                            { path: ':tab', element: <TrainingHubPage /> }
                          ]
                        }
                      ]
                    },
                    {
                      path: 'dashboard',
                      element: <OrgDashboardGuard accessibleRoles={CHAMPION_TENANT_MEMBERSHIP_ROLES} />,
                      children: [{ path: '', element: <ChampionDashboard /> }]
                    }
                  ]
                },
                {
                  element: <TenantRoleGuard accessibleRoles={ALL_TENANT_MEMBERSHIP_ROLES} />,
                  children: [
                    { path: 'reporting/dashboard/:dashboardId', element: <GroupDrilldown /> },
                    { path: 'reporting/dashboard/:dashboardId/group/:groupId', element: <GroupDrilldown /> },
                    {
                      path: 'modules/pam',
                      element: <TenantFeatureGuard featureKey="national_returns_hub" redirectTo="/home" />,
                      children: [
                        { path: '', element: <PamHomePage /> },
                        { path: ':submissionId', element: <PamModulePage /> },
                        { path: 'saq/:submissionSaqId', element: <PamSaqOverviewPage /> },
                        { path: 'q/:submissionQuestionId', element: <PamQuestionOverviewPage /> }
                      ]
                    },
                    { path: 'groups', element: <GroupsPage /> },
                    { path: 'groups/:groupId', element: <GroupHubPage /> },
                    { path: 'groups/:groupId/:tab', element: <GroupHubPage /> },
                    { path: 'groups/:groupId/:tab/:cardId', element: <GroupHubPage /> },
                    {
                      path: 'meetings/:meetingId',
                      element: <MeetingLayout />,
                      children: [
                        { index: true, element: <MeetingPage /> },
                        { path: 'actions', element: <MeetingActionsPage /> }
                      ]
                    },
                    { path: 'write/:external_id', element: <FinalDocumentPage /> },
                    { path: 'review/:id', element: <Review /> },
                    { path: 'wiki/:idorfilename', element: <DocumentView /> },
                    { path: 'actions', element: <ActionsPage /> },
                    {
                      path: 'meetings/:meetingId/finalise',
                      element: <MeetingLayout />,
                      children: [{ index: true, element: <FinaliseMeetingPage /> }]
                    },
                    { path: 'meeting_series/:id', element: <MeetingSeriesRedirect /> }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '*',
      element: <RootRedirect />
    }
  ]);
}

const TenantPrefixGuard = () => {
  const { tenantCode } = useParams();
  const location = useLocation();
  const { isAuthenticated, isInitialized, user } = useAuth();

  if (isReservedAppPrefix(tenantCode || '')) {
    const originalPath = withLocationParams(location.pathname, location);

    if (!isInitialized) {
      return null;
    }

    if (!isAuthenticated) {
      if (auth.currentUser) return null;
      return <Navigate to={loginRedirectTo(location)} replace />;
    }

    if (user?.tenants) {
      const tenants = user.tenants;
      if (tenants.length > 1) {
        return <TenantPickerPage tenants={tenants} originalPath={originalPath} />;
      }
      if (tenants.length === 1) {
        return <Navigate to={toTenantPath(originalPath, tenants[0].code)} replace />;
      }
    }
  }

  if (isInitialized && isAuthenticated && user?.tenants?.length > 0) {
    const isMember = user.tenants.some((t) => t.code === tenantCode);
    if (!isMember) {
      const pathWithoutTenant = location.pathname.replace(new RegExp(`^/${tenantCode}`), '') || '/home';
      const originalPath = withLocationParams(pathWithoutTenant, location);
      const tenants = user.tenants;

      if (tenants.length === 1) {
        return <Navigate to={toTenantPath(originalPath, tenants[0].code)} replace />;
      }

      return (
        <TenantPickerPage
          tenants={tenants}
          originalPath={originalPath}
          title={`Organisation '${tenantCode}' not found`}
          description="That organisation doesn't exist or you don't have access to it. Choose one of your organisations instead."
        />
      );
    }
  }

  return <Outlet />;
};

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../features/auth/Login')));
const LoginSSO = Loadable(lazy(() => import('../features/auth/LoginSSO')));
const RegisterForum = Loadable(lazy(() => import('../features/auth/components/RegisterForum')));
const FreemiumSignup = Loadable(lazy(() => import('../features/auth/FreemiumSignup')));
const VerifyFreemiumEmail = Loadable(lazy(() => import('../features/auth/VerifyFreemiumEmail')));
const Register = Loadable(lazy(() => import('../features/auth/components/register/Register')));
const ForgotPassword = Loadable(lazy(() => import('../features/auth/ForgotPassword')));
const FirebaseActionHandler = Loadable(lazy(() => import('../features/auth/FirebaseActionHandler')));
const ChatLayout = Loadable(lazy(() => import('../features/chat')));
const ChatLandingPage = Loadable(lazy(() => import('../features/chat/ChatLandingPage')));
const WikiLayout = Loadable(lazy(() => import('../features/wiki/index')));
const WritePage = Loadable(lazy(() => import('../features/write/index')));
const ReviewHome = Loadable(lazy(() => import('../features/review/ReviewHome')));
const Review = Loadable(lazy(() => import('../features/review/Review')));
const ReportingHome = Loadable(lazy(() => import('../features/reporting/ReportingHome')));
const GroupDrilldown = Loadable(lazy(() => import('../features/reporting/components/GroupDrilldown')));
const UserProfile = Loadable(lazy(() => import('../features/profile/index')));
const EditProfile = Loadable(lazy(() => import('../features/profile/components/EditProfile')));
const ProfileAppointments = Loadable(lazy(() => import('../features/profile/components/ProfileAppointments')));
const HelpSupport = Loadable(lazy(() => import('../features/profile/components/SupportAndDelete')));
const NotificationsPage = Loadable(lazy(() => import('../features/profile/components/NotificationsPage')));
const OrganisationSettingsPage = Loadable(
  lazy(() => import('../features/profile/components/OrganisationSettingsPage'))
);
const ProfileToSettingsRedirect = Loadable(lazy(() => import('../features/profile/ProfileToSettingsRedirect')));
const ChampionDashboard = Loadable(lazy(() => import('../features/dashboard')));
const HelpLayout = Loadable(lazy(() => import('../features/tutorials/HelpLayout')));
const HelpHome = Loadable(lazy(() => import('../features/tutorials/HelpHome')));
const TutorialDetail = Loadable(lazy(() => import('../features/tutorials/TutorialDetail')));
const DocumentView = Loadable(lazy(() => import('../features/wiki/DocumentView')));
const FinalDocumentPage = Loadable(lazy(() => import('../features/write/shared/FinalDocumentPage')));
const MicrosoftAuthCallback = Loadable(lazy(() => import('../features/auth/MicrosoftAuthCallback')));
const AdminHome = Loadable(lazy(() => import('../features/admin/home')));
const DocumentHubPage = Loadable(lazy(() => import('../features/document-hub/index')));
const DocumentHubAlertsPage = Loadable(lazy(() => import('../features/document-hub/components/AlertsPage')));
const GroupsPage = Loadable(lazy(() => import('../features/groups/index')));
const ActionsPage = Loadable(lazy(() => import('../features/groups/ActionsPage')));
const GroupHubPage = Loadable(lazy(() => import('../features/groups/GroupHubPage')));
const UnsubscribeRemindersPage = Loadable(lazy(() => import('../features/groups/UnsubscribeRemindersPage')));
const MeetingPage = Loadable(lazy(() => import('../features/meetings/MeetingPage')));
const MeetingActionsPage = Loadable(lazy(() => import('../features/meetings/MeetingActionsPage')));
const FinaliseMeetingPage = Loadable(lazy(() => import('../features/meetings/FinaliseMeetingPage')));
const MeetingLayout = Loadable(lazy(() => import('../features/meetings/MeetingLayout')));
const MeetingSeriesRedirect = Loadable(lazy(() => import('../features/meetings/MeetingSeriesRedirect')));
const VerifyEmailChange = Loadable(lazy(() => import('../features/profile/components/VerifyEmailChange')));
const ActivityFeed = Loadable(lazy(() => import('../features/profile/components/ActivityFeed')));
// Modules / PFI
const PFIModulePage = Loadable(lazy(() => import('../features/pfi/PFIModulePage')));
const AssetReviewListPage = Loadable(lazy(() => import('../features/pfi/AssetReviewListPage')));
const AssetReviewDetailPage = Loadable(lazy(() => import('../features/pfi/AssetReviewDetailPage')));
const ObligationRegisterPage = Loadable(lazy(() => import('../features/pfi/ObligationRegisterPage')));
const TrainingHubPage = Loadable(lazy(() => import('../features/training-hub/TrainingHubPage')));
// Modules / PAM
const PamHomePage = Loadable(lazy(() => import('../features/pam/PamHomePage')));
const PamModulePage = Loadable(lazy(() => import('../features/pam/PamModulePage')));
const PamSaqOverviewPage = Loadable(lazy(() => import('../features/pam/PamSaqOverviewPage')));
const PamQuestionOverviewPage = Loadable(lazy(() => import('../features/pam/question/PamQuestionOverviewPage')));
const NoTenantPage = Loadable(lazy(() => import('../features/home/NoTenantPage')));
// Dashboard
